import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useEntityOptions } from '../hooks/useEntityOptions';
import { EntitySelect } from '../components/shared/EntitySelect';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import {
  FileText, Plus, Search, DollarSign, Clock, CheckCircle2, AlertTriangle,
  Trash2, Pencil, Eye, RefreshCw, Send, Download, Receipt
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExportButton } from '../components/shared/ExportButton';
import { ContactLinker } from '../components/shared/ContactLinker';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import ProductLookupDropdown from '../components/products/ProductLookupDropdown';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: Trash2 },
};

const StatCard = ({ icon: Icon, label, value, color, prefix = '' }) => (
  <Card className="bg-white border-gray-border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-medium">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-text-primary mt-1">{prefix}{value}</p>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const EMPTY_LINE = { description: '', quantity: 1, unit_price: 0 };

export default function InvoiceGeneratorPage() {
  const { format: fc, symbol: currSymbol, code: userCurrency } = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [form, setForm] = useState({ client_name: '', client_email: '', due_date: '', line_items: [{ ...EMPTY_LINE }], tax_rate: 0, discount: 0, notes: '', payment_terms: 'Net 30', currency: userCurrency || 'USD' });
  const { contacts } = useEntityOptions();

  const [clientList, setClientList] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/clients/dropdown`).then(r => setClientList(r.data || [])).catch(() => {});
  }, []);

  const handleClientSelectInvoice = (clientId) => {
    const c = clientList.find(cl => cl.id === clientId);
    if (c) setForm(f => ({ ...f, client_name: c.contact_name || '', client_email: c.email || '' }));
  };

  const fetchData = useCallback(async () => {
    try {
      const [inv, dash] = await Promise.all([
        axios.get(`${API}/api/invoices`),
        axios.get(`${API}/api/invoices/dashboard`),
      ]);
      setInvoices(inv.data.invoices || []);
      setDashboard(dash.data);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) => setForm(f => {
    const items = [...f.line_items];
    items[i] = { ...items[i], [field]: field === 'description' ? val : Number(val) || 0 };
    return { ...f, line_items: items };
  });

  const subtotal = form.line_items.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const taxAmount = subtotal * (form.tax_rate / 100);
  const total = subtotal + taxAmount - form.discount;

  const handleCreate = async () => {
    if (!form.client_name.trim()) { toast.error('Client name is required'); return; }
    try {
      await axios.post(`${API}/api/invoices`, form);
      toast.success('Invoice created!');
      setShowCreate(false);
      setForm({ client_name: '', client_email: '', due_date: '', line_items: [{ ...EMPTY_LINE }], tax_rate: 0, discount: 0, notes: '', payment_terms: 'Net 30', currency: userCurrency || 'USD' });
      fetchData();
    } catch { toast.error('Failed to create invoice'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/api/invoices/${id}`, { status });
      toast.success(`Invoice marked as ${status}`);
      fetchData();
      setShowView(null);
    } catch { toast.error('Failed to update'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/api/invoices/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    if (search && !inv.client_name?.toLowerCase().includes(search.toLowerCase()) && !inv.invoice_number?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-6 w-6 animate-spin text-gray-medium" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoice-generator-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Invoices<PageInfoTooltip pageKey="invoices" /></h1>
          <p className="text-gray-medium text-sm mt-1">Create and manage invoices for your clients</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            data={invoices.map(inv => ({ number: inv.invoice_number, client: inv.client_name, amount: inv.total, status: inv.status, date: inv.issue_date }))}
            filename="invoices"
            columns={['number', 'client', 'amount', 'status', 'date']}
          />
          <Button onClick={() => setShowCreate(true)} data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Receipt} label="Total Invoices" value={dashboard.total || 0} color="bg-blue-50 text-blue-600" />
        <StatCard icon={DollarSign} label="Total Invoiced" value={fc(dashboard.total_invoiced || 0)} color="bg-purple-50 text-purple-600" />
        <StatCard icon={CheckCircle2} label="Total Paid" value={fc(dashboard.total_paid || 0)} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={AlertTriangle} label="Outstanding" value={fc(dashboard.outstanding || 0)} color="bg-amber-50 text-amber-600" />
      </div>

      <Card className="bg-white border-gray-border">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search invoices..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="invoice-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]" data-testid="invoice-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-medium">
              <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No invoices yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(inv => {
                const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                return (
                  <div key={inv.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setShowView(inv)} data-testid={`invoice-row-${inv.id}`}>
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-text-primary">{inv.invoice_number}</span>
                        <Badge className={cn("text-[10px]", cfg.color)}>{cfg.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-medium truncate">{inv.client_name} {inv.client_email ? `· ${inv.client_email}` : ''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-sm text-text-primary">{fc(inv.total || 0)}</p>
                      <p className="text-xs text-gray-medium">{inv.due_date || '—'}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); deleteInvoice(inv.id); }}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Client Selector */}
            {clientList.length > 0 && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <Label className="text-xs font-semibold mb-1 block">Select Client</Label>
                <Select onValueChange={handleClientSelectInvoice}>
                  <SelectTrigger className="h-8 text-sm" data-testid="invoice-client-dropdown"><SelectValue placeholder="Select a client to auto-fill..." /></SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {clientList.map(c => <SelectItem key={c.id} value={c.id}>{c.contact_name} {c.company_name ? `(${c.company_name})` : ''} {c.email ? `— ${c.email}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client Name *</Label><Input value={form.client_name} onChange={v => setForm(f => ({ ...f, client_name: typeof v === 'string' ? v : v.target.value }))} placeholder="Auto-filled from client selection" data-testid="invoice-client-name" /></div>
              <div><Label>Client Email</Label><Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
              <div><Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={v => setForm(f => ({ ...f, payment_terms: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items</Label>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="h-3 w-3 mr-1" />Add</Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((line, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <div className="flex-1 space-y-1">
                      <ProductLookupDropdown
                        value={line.description}
                        placeholder="Search product / service catalog…"
                        testIdPrefix={`invoice-line-${i}-product`}
                        onSelect={(p) => {
                          const items = [...form.line_items];
                          items[i] = {
                            ...items[i],
                            description: p.name,
                            unit_price: Number(p.price) || items[i].unit_price,
                            product_id: p.id,
                            sku: p.sku || '',
                          };
                          setForm(f => ({ ...f, line_items: items }));
                        }}
                      />
                      <Input placeholder="Description (optional override)" value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} className="text-xs" data-testid={`invoice-line-${i}-desc`} />
                    </div>
                    <div className="w-20"><Input type="number" min={1} placeholder="Qty" value={line.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></div>
                    <div className="w-28"><Input type="number" min={0} step={0.01} placeholder="Price" value={line.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} /></div>
                    <div className="w-20 text-right text-sm font-medium pt-2">{fc(line.quantity * line.unit_price)}</div>
                    {form.line_items.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeLine(i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tax Rate (%)</Label><Input type="number" min={0} max={100} value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: Number(e.target.value) }))} /></div>
              <div><Label>Discount ({currSymbol})</Label><Input type="number" min={0} value={form.discount} onChange={e => setForm(f => ({ ...f, discount: Number(e.target.value) }))} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-medium">Subtotal</span><span className="font-medium">{fc(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-gray-medium">Tax ({form.tax_rate}%)</span><span>{fc(taxAmount)}</span></div>
              {form.discount > 0 && <div className="flex justify-between"><span className="text-gray-medium">Discount</span><span className="text-red-500">-{fc(form.discount)}</span></div>}
              <div className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span className="text-emerald-600">{fc(total)}</span></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} data-testid="save-invoice-btn">Create Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{showView?.invoice_number}</DialogTitle></DialogHeader>
          {showView && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-medium">Client:</span> <span className="font-medium">{showView.client_name}</span></div>
                <div><span className="text-gray-medium">Email:</span> <span>{showView.client_email || '—'}</span></div>
                <div><span className="text-gray-medium">Issue Date:</span> <span>{showView.issue_date}</span></div>
                <div><span className="text-gray-medium">Due Date:</span> <span>{showView.due_date || '—'}</span></div>
                <div><span className="text-gray-medium">Status:</span> <Badge className={cn("text-[10px]", (STATUS_CONFIG[showView.status] || STATUS_CONFIG.draft).color)}>{(STATUS_CONFIG[showView.status] || STATUS_CONFIG.draft).label}</Badge></div>
                <div><span className="text-gray-medium">Terms:</span> <span>{showView.payment_terms}</span></div>
              </div>
              {showView.line_items?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50"><tr><th className="text-left p-2 text-gray-medium font-medium">Item</th><th className="text-right p-2 text-gray-medium font-medium">Qty</th><th className="text-right p-2 text-gray-medium font-medium">Price</th><th className="text-right p-2 text-gray-medium font-medium">Total</th></tr></thead>
                    <tbody>
                      {showView.line_items.map((l, i) => <tr key={i} className="border-t"><td className="p-2">{l.description || '—'}</td><td className="text-right p-2">{l.quantity}</td><td className="text-right p-2">{fc(l.unit_price)}</td><td className="text-right p-2 font-medium">{fc(l.quantity * l.unit_price)}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-medium">Subtotal</span><span>{fc(showView.subtotal || 0)}</span></div>
                <div className="flex justify-between"><span className="text-gray-medium">Tax</span><span>{fc(showView.tax_amount || 0)}</span></div>
                {showView.discount > 0 && <div className="flex justify-between"><span className="text-gray-medium">Discount</span><span className="text-red-500">-{fc(showView.discount)}</span></div>}
                <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span className="text-emerald-600">{fc(showView.total || 0)}</span></div>
              </div>
              <div className="flex flex-wrap gap-2">
                {showView.status === 'draft' && <Button size="sm" onClick={() => updateStatus(showView.id, 'sent')} data-testid="mark-sent-btn"><Send className="h-3.5 w-3.5 mr-1" />Mark Sent</Button>}
                {['sent', 'viewed'].includes(showView.status) && <Button size="sm" onClick={() => updateStatus(showView.id, 'paid')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-paid-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Paid</Button>}
                {showView.status !== 'cancelled' && showView.status !== 'paid' && <Button size="sm" variant="outline" onClick={() => updateStatus(showView.id, 'overdue')}>Mark Overdue</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
